import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { post } from "../lib/api";
import { leadSourceFields } from "../lib/leadSource";
import { measureLead } from "../lib/openaiPixel";
import { Eyebrow, MaskLine } from "../components/ui-bits";
import { useLang } from "../i18n/LangContext";

const field =
  "w-full rounded-lg border border-sand bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-fog/60 focus:border-verde-700";
const label = "mb-1.5 block text-xs font-medium text-ink";

const COPY = {
  en: {
    types: ["Apartments", "Student Housing", "Senior Housing", "Co-Living", "Serviced Apartments", "Hotels"],
    stages: ["Land acquisition", "Licensing / planning", "Pre-sales / pre-construction"],
    heroEyebrow: "Broker submission",
    heroH: "Submit a deal.",
    heroCopy: "A few minutes now. We review the deal and email you our Broker & Advisor Partnership Agreement to sign.",
    you: "About you",
    deal: "About the deal",
    name: "Name *",
    company: "Company",
    email: "Email *",
    phone: "Phone",
    location: "Project location *",
    locationPh: "e.g. Lisbon",
    type: "Project type *",
    typePh: "Select project type",
    stage: "Project stage *",
    stagePh: "Select stage",
    amount: "Financing required *",
    amountPh: "e.g. €2,000,000",
    notes: "Notes",
    error: "Something went wrong. Please try again.",
    submitting: "Submitting…",
    submit: "Submit & request agreement",
    agreementNote: "No signature needed today. We email you the Broker & Advisor Partnership Agreement — sign it and you're on our partner register, with your introductions protected for 12 months.",
    doneH: "Deal received.",
    doneCopy:
      "Thank you. Our credit team will review the opportunity — and we're emailing your Broker & Advisor Partnership Agreement to your inbox for signature. Once signed, you're on our partner register and your introductions are protected for 12 months from today.",
    doneRef: "Your reference",
    doneHome: "Back to home",
  },
  pt: {
    types: ["Apartamentos", "Habitação para Estudantes", "Habitação para Seniores", "Co-Living", "Apartamentos de Serviços", "Hotéis"],
    stages: ["Aquisição de terreno", "Licenciamento / planeamento", "Pré-vendas / pré-construção"],
    heroEyebrow: "Apresentação de intermediário",
    heroH: "Apresentar um negócio.",
    heroCopy: "Alguns minutos agora. Analisamos a oportunidade e enviamos-lhe o nosso Acordo de Parceria para Intermediários e Consultores para assinar.",
    you: "Sobre si",
    deal: "Sobre o negócio",
    name: "Nome *",
    company: "Empresa",
    email: "Email *",
    phone: "Telefone",
    location: "Localização do projeto *",
    locationPh: "ex.: Lisboa",
    type: "Tipo de projeto *",
    typePh: "Selecione o tipo de projeto",
    stage: "Fase do projeto *",
    stagePh: "Selecione a fase",
    amount: "Financiamento necessário *",
    amountPh: "ex.: €2.000.000",
    notes: "Notas",
    error: "Ocorreu um erro. Tente novamente, por favor.",
    submitting: "A apresentar…",
    submit: "Apresentar & pedir acordo",
    agreementNote: "Sem assinaturas hoje. Enviamos-lhe o Acordo de Parceria para Intermediários e Consultores — assine e fica no nosso registo de parceiros, com as suas apresentações protegidas durante 12 meses.",
    doneH: "Negócio recebido.",
    doneCopy:
      "Obrigado. A equipa de crédito irá analisar a oportunidade — e enviamos para o seu email o nosso Acordo de Parceria para Intermediários e Consultores para assinatura. Assinado o acordo, fica no nosso registo de parceiros e as suas apresentações ficam protegidas durante 12 meses a partir de hoje.",
    doneRef: "A sua referência",
    doneHome: "Voltar ao início",
  },
  nl: {
    types: ["Appartementen", "Studentenhuisvesting", "Seniorenwoningen", "Co-Living", "Serviced apartments", "Hotels"],
    stages: ["Grondverwerving", "Vergunningen / bestemmingsplan", "Voorverkoop / voorbouwfase"],
    heroEyebrow: "Introductie tussenpersoon",
    heroH: "Een deal indienen.",
    heroCopy: "Enkele minuten nu. Wij beoordelen de deal en mailen u onze Partnerovereenkomst voor Tussenpersonen & Adviseurs ter ondertekening.",
    you: "Over u",
    deal: "Over de deal",
    name: "Naam *",
    company: "Bedrijf",
    email: "E-mail *",
    phone: "Telefoon",
    location: "Projectlocatie *",
    locationPh: "bijv. Lissabon",
    type: "Projecttype *",
    typePh: "Kies een projecttype",
    stage: "Projectfase *",
    stagePh: "Kies een fase",
    amount: "Benodigde financiering *",
    amountPh: "bijv. €2.000.000",
    notes: "Opmerkingen",
    error: "Er ging iets mis. Probeer het opnieuw.",
    submitting: "Indienen…",
    submit: "Indienen & overeenkomst aanvragen",
    agreementNote: "Vandaag geen handtekening nodig. Wij mailen u de Partnerovereenkomst voor Tussenpersonen & Adviseurs — onderteken en u staat op ons partnerregister, met 12 maanden bescherming voor uw introducties.",
    doneH: "Deal ontvangen.",
    doneCopy:
      "Bedankt. Ons kredietteam beoordeelt de kans — en wij mailen uw Partnerovereenkomst voor Tussenpersonen & Adviseurs ter ondertekening naar uw inbox. Eenmaal getekend staat u op ons partnerregister en zijn uw introducties 12 maanden vanaf vandaag beschermd.",
    doneRef: "Uw referentie",
    doneHome: "Terug naar de homepage",
  },
};

export default function BrokerSubmit() {
  const { lang, lp } = useLang();
  const t = COPY[lang];
  const [form, setForm] = useState({
    broker_name: "", broker_company: "", email: "", phone: "",
    project_location: "", project_type: "", project_stage: "",
    amount_required: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.broker_name.trim() && /.+@.+\..+/.test(form.email) && form.project_location.trim() && form.project_type && form.project_stage && form.amount_required.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await post("/broker-submissions", { ...form, ...leadSourceFields() });
      measureLead(res?.data?.event_id);
      setReference(res.data.reference);
      window.scrollTo(0, 0);
    } catch {
      setError(t.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6 pt-24 pb-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-xl w-full text-center" data-testid="broker-confirmation">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-verde-800">
            <Check className="h-6 w-6 text-mint" />
          </span>
          <h1 className="display mt-8 text-4xl sm:text-5xl text-charcoal">{t.doneH}</h1>
          <p className="mt-5 text-ink leading-relaxed">{t.doneCopy}</p>
          <div className="mt-8 inline-block rounded-2xl border border-sand bg-white px-8 py-5">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-fog">{t.doneRef}</p>
            <p className="mt-2 font-mono text-xl tracking-[0.14em] text-verde-800" data-testid="broker-reference">{reference}</p>
          </div>
          <div className="mt-10">
            <Link to={lp("/")} className="text-sm font-semibold text-verde-800 underline underline-offset-4">{t.doneHome}</Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <section className="pt-32 sm:pt-40 pb-10">
        <div className="container-x max-w-3xl">
          <Eyebrow>{t.heroEyebrow}</Eyebrow>
          <h1 className="display mt-6 text-[2.4rem] sm:text-5xl text-charcoal">
            <MaskLine>{t.heroH}</MaskLine>
          </h1>
          <p className="mt-5 text-ink max-w-xl">{t.heroCopy}</p>
        </div>
      </section>

      <section className="pb-28">
        <div className="container-x max-w-3xl">
          <form onSubmit={submit} className="rounded-2xl bg-white border border-sand p-6 sm:p-10 space-y-6" data-testid="broker-form">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-verde-700">{t.you}</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={label}>{t.name}</label>
                  <input className={field} value={form.broker_name} onChange={set("broker_name")} data-testid="broker-name" />
                </div>
                <div>
                  <label className={label}>{t.company}</label>
                  <input className={field} value={form.broker_company} onChange={set("broker_company")} />
                </div>
                <div>
                  <label className={label}>{t.email}</label>
                  <input type="email" className={field} value={form.email} onChange={set("email")} data-testid="broker-email" />
                </div>
                <div>
                  <label className={label}>{t.phone}</label>
                  <input className={field} value={form.phone} onChange={set("phone")} />
                </div>
              </div>
            </div>

            <div className="border-t border-sand pt-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-verde-700">{t.deal}</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={label}>{t.location}</label>
                  <input className={field} value={form.project_location} onChange={set("project_location")} placeholder={t.locationPh} data-testid="broker-location" />
                </div>
                <div>
                  <label className={label}>{t.type}</label>
                  <select className={field} value={form.project_type} onChange={set("project_type")} data-testid="broker-type">
                    <option value="" disabled>{t.typePh}</option>
                    {t.types.map((v) => <option key={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>{t.stage}</label>
                  <select className={field} value={form.project_stage} onChange={set("project_stage")} data-testid="broker-stage">
                    <option value="" disabled>{t.stagePh}</option>
                    {t.stages.map((v) => <option key={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>{t.amount}</label>
                  <input className={field} value={form.amount_required} onChange={set("amount_required")} placeholder={t.amountPh} data-testid="broker-amount" />
                </div>
              </div>
              <div className="mt-6">
                <label className={label}>{t.notes}</label>
                <textarea rows={4} className={field} value={form.notes} onChange={set("notes")} data-testid="broker-notes" />
              </div>
            </div>

            {error && <p className="text-sm text-red-700" data-testid="broker-error">{error}</p>}

            <button
              type="submit"
              disabled={!valid || submitting}
              className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-verde-800 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-verde-700 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="broker-submit"
            >
              {submitting ? t.submitting : t.submit}
              {!submitting && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
            </button>
            <p className="text-xs leading-relaxed text-fog" data-testid="broker-agreement-note">{t.agreementNote}</p>
          </form>
        </div>
      </section>
    </div>
  );
}
